import {
    ZfCard,
    ZfCardMedia,
    ZfDialog,
    ZfDialogContent,
    ZfDialogFooter,
    ZfDialogHeader,
    ZfFormField,
    ZfInput,
    ZfLoadingSpinner,
    ZfSelectOption,
    ZfStack,
    ZfTextButton,
} from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getImageUrlByString } from '../../../../styles/assets/imageMap';
import { useTopic } from '../../../api';
import type { EventStatus, SurplusType } from '../../../api/generated/client';
import type { AdvisoryOrLifeEventType, IAdvisoryOrLifeEventFormValues } from '../../../interfaces/event';
import { getIsoDateFromDateString } from '../../../utils/date-utils';
import { isAdvisoryEventNew } from '../../../utils/eventUtils';
import { requiredField } from '../../../utils/form-utils';
import { useEventApiRequest } from '../../modal/eventModal/useEventApiRequest';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { NeedEventModalDateFields } from './NeedEventModalDateFields';
import { NeedEventModalSecondaryButton } from './NeedEventModalSecondaryButton';
import { NeedEventModalTypeAmountFields } from './NeedEventModalTypeAmountFields';

const eventStatusOptions: EventStatus[] = ['DEFINED', 'IN_PROGRESS', 'COMPLETED_SUCCESSFULLY', 'COMPLETED_STOPPED'] as const;

interface IProps {
    event?: AdvisoryOrLifeEventType;
    onClose: () => void;
    onBack?: () => void;
    isOpen: boolean;
}

export const NeedEventModal: FunctionComponent<IProps> = ({ event, onClose, onBack, isOpen }) => {
    const { t, i18n } = useTranslation();

    const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState<boolean>(false);

    const { data: topic } = useTopic(event?.topicId);
    const isAddingNewEntry = event?.id == null || event.id === -1;

    const { deleteEvent, updateEvent, createEvent, inProgress } = useEventApiRequest(onClose);

    const { control, handleSubmit, reset, setValue, trigger } = useForm<IAdvisoryOrLifeEventFormValues>({
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: {
            name: event?.name,
            dueDate: event?.dueDate ? getIsoDateFromDateString(event.dueDate) : undefined,
            reminderDate: event?.reminderDate ? getIsoDateFromDateString(event.reminderDate) : undefined,
            isCashFlowRelevant: !!event?.isCashFlowRelevant,
            amount: event?.amount,
            type: event?.isCashFlowRelevant ? event?.type : 'NON_CASH_FLOW',
            currency: event?.currency,
            note: event?.note,
            status: event?.status ?? 'DEFINED',
        },
    });
    const { isDirty, isValid } = useFormState<IAdvisoryOrLifeEventFormValues>({ control });
    const isCashRelevant = useWatch<IAdvisoryOrLifeEventFormValues>({ control, name: 'isCashFlowRelevant' });

    const handleReset = () => {
        reset({
            name: event?.name,
            dueDate: event?.dueDate ? getIsoDateFromDateString(event.dueDate) : undefined,
            reminderDate: event?.reminderDate ? getIsoDateFromDateString(event.reminderDate) : undefined,
            isCashFlowRelevant: !!event?.isCashFlowRelevant,
            amount: event?.amount,
            type: event?.isCashFlowRelevant ? event?.type : 'NON_CASH_FLOW',
            currency: event?.currency,
            note: event?.note,
            status: event?.status ?? 'DEFINED',
        });
    };

    const performDelete = () => {
        deleteEvent(event?.id, event?.eventType);
        onClose();
    };

    const submitHandler = handleSubmit(({ name, dueDate, reminderDate, isCashFlowRelevant, amount, type, currency, note, status }) => {
        if (!isValid) return;

        const updatedEvent: AdvisoryOrLifeEventType = {
            ...event,
            name,
            dueDate,
            reminderDate,
            isCashFlowRelevant,
            amount: isCashFlowRelevant ? amount : undefined,
            type: type,
            status: status || 'DEFINED',
            notification: 'NONE',
            currency,
            note,
        };

        if (updatedEvent?.id == null || updatedEvent.id === -1) createEvent(updatedEvent);
        else updateEvent(updatedEvent);

        onClose();
    });

    return (
        <>
            <ZfDialog disableLightDismiss={true} showCloseButton={false} show={isOpen && !showConfirmDeleteDialog} size="m" className="event-modal">
                <ZfDialogHeader>
                    {isAddingNewEntry
                        ? t(isAdvisoryEventNew(event) ? 'zfEventModalNewNeed' : 'zfEventModalNewEvent')
                        : t(isAdvisoryEventNew(event) ? 'zfEventModalEditNeed' : 'zfEventModalEditEvent')}
                </ZfDialogHeader>
                <ZfDialogContent>
                    {inProgress ? (
                        <ZfLoadingSpinner />
                    ) : (
                        <div className="event-modal__content">
                            <div className="d-flex">
                                <div className="event-modal__card">
                                    <ZfCard>
                                        <ZfCardMedia image={getImageUrlByString(topic?.key)}>
                                            <label>{topic?.name[i18n.language]}</label>
                                        </ZfCardMedia>
                                    </ZfCard>
                                </div>
                                <div className="event-modal__line_1_2">
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{ required: requiredField(t('textFieldRequired')) }}
                                        render={({ field, fieldState }) => (
                                            <ZfFormField
                                                label={t('lifeEventModalTitleLabel')}
                                                mandatory={true}
                                                className="mt-auto"
                                                alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                                            >
                                                <ZfInput
                                                    value={field.value}
                                                    counter={true}
                                                    maxLength={75}
                                                    error={fieldState.invalid}
                                                    onInputChange={(e) => field.onChange(e.detail)}
                                                    onBlur={field.onBlur}
                                                />
                                            </ZfFormField>
                                        )}
                                    />
                                    {!isAdvisoryEventNew(event) ? (
                                        <NeedEventModalTypeAmountFields
                                            currency={event?.currency}
                                            isCashFlowRelevant={!!isCashRelevant}
                                            control={control}
                                            setFormValue={setValue}
                                        />
                                    ) : (
                                        <NeedEventModalDateFields
                                            eventType={event.eventType}
                                            control={control}
                                            setFormValue={setValue}
                                            triggerValidation={trigger}
                                        />
                                    )}
                                </div>
                            </div>
                            {!isAdvisoryEventNew(event) ? (
                                <NeedEventModalDateFields eventType={event?.eventType} control={control} setFormValue={setValue} triggerValidation={trigger} />
                            ) : null}
                            <Controller
                                name="status"
                                control={control}
                                rules={{ required: requiredField(t('selectFieldRequired')) }}
                                render={({ field, fieldState }) => (
                                    <ZfFormField
                                        label={t('eventModalStatus')}
                                        mandatory={true}
                                        className="w-100"
                                        alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                                    >
                                        <ZfInput
                                            type="select"
                                            value={field.value}
                                            error={fieldState.invalid}
                                            onSelectChange={(e) => field.onChange(e.detail as EventStatus)}
                                            onBlur={field.onBlur}
                                        >
                                            {eventStatusOptions.map((status) => (
                                                <ZfSelectOption key={status} value={status} description={t(`eventStatus-${status}`)} />
                                            ))}
                                        </ZfInput>
                                    </ZfFormField>
                                )}
                            />
                            <Controller
                                name="note"
                                control={control}
                                render={({ field }) => (
                                    <ZfFormField label={t('eventModalCommentNextSteps')}>
                                        <ZfInput
                                            placeholder={t('eventModalAddComment')}
                                            value={field.value}
                                            multiline={true}
                                            counter={true}
                                            maxLength={200}
                                            rows={2}
                                            onInputChange={(e) => field.onChange(e.detail as SurplusType)}
                                            onBlur={field.onBlur}
                                        />
                                    </ZfFormField>
                                )}
                            />
                        </div>
                    )}
                </ZfDialogContent>
                <ZfDialogFooter>
                    <ZfStack slot="secondary" direction="row" spacing="small">
                        <NeedEventModalSecondaryButton
                            addingNewEntry={isAddingNewEntry}
                            handleDelete={() => setShowConfirmDeleteDialog(true)}
                            handleReset={handleReset}
                            onBack={onBack}
                        />
                    </ZfStack>
                    <ZfStack slot="primary" direction="row" spacing="small">
                        <ZfTextButton size="medium" hierarchy="tertiary" onClick={onClose}>
                            {t('cancel')}
                        </ZfTextButton>
                        <ZfTextButton size="medium" kind="standard" disabled={!isValid || !isDirty} onClick={submitHandler}>
                            {isAddingNewEntry
                                ? t(isCashRelevant ? 'eventModalAddToTimelineButton' : 'eventModalAddToRoadmapButton')
                                : t('eventModalUpdateButton')}
                        </ZfTextButton>
                    </ZfStack>
                </ZfDialogFooter>
            </ZfDialog>
            <ConfirmDeleteModal isOpen={showConfirmDeleteDialog} onDelete={performDelete} onClose={onClose} />
        </>
    );
};
